import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useCompany } from "./useCompany";

export interface RecurringExpense {
  id: string;
  company_id: string;
  user_id: string;
  vehicle_id: string | null;
  loan_id: string | null;
  amount: number;
  loan_payment_type: string | null;
  description: string | null;
  frequency: string;
  start_date: string;
  next_generation_date: string;
  is_active: boolean;
  expense_type: string | null;
  paid_by: string | null;
  paid_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface RecurringExpenseFormData {
  vehicle_id?: string | null;
  loan_id?: string | null;
  amount: number;
  description?: string;
  frequency: string;
  start_date: string;
  next_generation_date?: string;
  is_active?: boolean;
  expense_type?: string | null;
  paid_by?: string | null;
  paid_to?: string | null;
  loan_payment_type?: string | null;
}

export const FREQUENCY_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "bi-weekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

export const useRecurringExpenses = () => {
  const queryClient = useQueryClient();
  const { company } = useCompany();

  const { data: recurringExpenses = [], isLoading } = useQuery({
    queryKey: ["recurring-expenses", company?.id],
    enabled: !!company?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("recurring_expenses")
        .select("*")
        .eq("company_id", company!.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as RecurringExpense[];
    },
  });

  const addRecurringExpense = useMutation({
    mutationFn: async (formData: RecurringExpenseFormData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      if (!company?.id) throw new Error("No company found");

      const insertData = {
        ...formData,
        next_generation_date: formData.next_generation_date || formData.start_date,
        company_id: company.id,
        user_id: user.id,
      };
      const { data, error } = await supabase
        .from("recurring_expenses")
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-expenses"] });
      toast.success("Recurring expense template created");
    },
    onError: (error) => {
      toast.error("Failed to create recurring expense: " + error.message);
    },
  });

  const updateRecurringExpense = useMutation({
    mutationFn: async ({ id, ...data }: Partial<RecurringExpenseFormData> & { id: string }) => {
      const { data: result, error } = await supabase
        .from("recurring_expenses")
        .update(data)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-expenses"] });
      toast.success("Recurring expense updated");
    },
    onError: (error) => {
      toast.error("Failed to update recurring expense: " + error.message);
    },
  });

  const deleteRecurringExpense = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("recurring_expenses")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-expenses"] });
      toast.success("Recurring expense deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete recurring expense: " + error.message);
    },
  });

  const backfillAndCreate = useMutation({
    mutationFn: async ({
      formData,
      backfillDates,
    }: {
      formData: RecurringExpenseFormData;
      backfillDates: string[]; // ISO date strings
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      if (!company?.id) throw new Error("No company found");

      // Insert the recurring expense template
      const insertData = {
        ...formData,
        next_generation_date: formData.next_generation_date || formData.start_date,
        company_id: company.id,
        user_id: user.id,
      };
      const { data: template, error: templateError } = await supabase
        .from("recurring_expenses")
        .insert(insertData)
        .select()
        .single();

      if (templateError) throw templateError;

      // Batch-insert backfill expense records if any
      if (backfillDates.length > 0) {
        const expenseRows = backfillDates.map((date) => ({
          amount: formData.amount,
          expense_name: formData.description || null,
          expense_date: date,
          expense_type: formData.expense_type || null,
          vehicle_id: formData.vehicle_id || null,
          loan_id: formData.loan_id || null,
          loan_payment_type: formData.loan_payment_type || null,
          paid_by: formData.paid_by || null,
          paid_to: formData.paid_to || null,
          recurring_expense_id: template.id,
          company_id: company.id,
          user_id: user.id,
        }));

        const { error: expenseError } = await supabase
          .from("expenses")
          .insert(expenseRows);

        if (expenseError) throw expenseError;
      }

      return template;
    },
    onSuccess: (_, { backfillDates }) => {
      queryClient.invalidateQueries({ queryKey: ["recurring-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      const msg = backfillDates.length > 0
        ? `Recurring expense created with ${backfillDates.length} backfilled expense${backfillDates.length === 1 ? "" : "s"}`
        : "Recurring expense template created";
      toast.success(msg);
    },
    onError: (error) => {
      toast.error("Failed to create recurring expense: " + error.message);
    },
  });

  return {
    recurringExpenses,
    isLoading,
    addRecurringExpense,
    updateRecurringExpense,
    deleteRecurringExpense,
    backfillAndCreate,
  };
};
